import { useCallback } from 'react';
import { useStatusFeatureByKey } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { UpsellResultEnum } from 'domains/Billing/UpsellFlow/hooks/useUpsellFlow';
import { useUpsellProtectionConfig } from './UpsellProtectionContext';

interface UseUpsellProtectedActionsOptions {
  onUpsellDecline?: () => void;
}

interface UseUpsellProtectedActionsReturn {
  hasFeature: boolean;
  withUpsellCheck: <T extends (...args: any[]) => any>(fn: T) => T;
}

function useUpsellProtectedActions({
  onUpsellDecline,
}: UseUpsellProtectedActionsOptions = {}): UseUpsellProtectedActionsReturn {
  const config = useUpsellProtectionConfig();

  const { isAvailable: hasFeature } = useStatusFeatureByKey(
    config.featureKey || '',
  );

  const triggerUpsellFlow = useUpsellFlow({
    featureKey: config.featureKey || '',
    trackingSource: config.trackingSource || '',
    title: config.title || '',
    asAside: true,
    onCloseModal: (result: UpsellResultEnum) => {
      if (result === UpsellResultEnum.DECLINED && onUpsellDecline) {
        onUpsellDecline();
      }
    },
  });

  const withUpsellCheck = useCallback(
    <T extends (...args: any[]) => any>(fn: T): T => {
      if (!config.enabled || !config.featureKey) {
        return fn;
      }

      return ((...args: any[]) => {
        if (hasFeature) {
          return fn(...args);
        }
        triggerUpsellFlow();
        return undefined;
      }) as T;
    },
    [config.enabled, config.featureKey, hasFeature, triggerUpsellFlow],
  );

  return {
    hasFeature: config.enabled && config.featureKey ? hasFeature : true,
    withUpsellCheck,
  };
}

export default useUpsellProtectedActions;
